import { useEffect, useRef, useState } from "react";
import { useMapColorState } from "../../../store/MapColorState";
import { getZoomRange } from "../helper/MapUtil";
import useEffectSkipFirst from "../../../hooks/useEffectSkipFirst";
import WRAP from "WRAP-JS";
import { useRecoilValue } from "recoil";
import { googleMapLanguageState, googleMapStyleState, googleMapThemeNameState, GOOGLE_MAP_THEME } from "../../../store/GoogleMapState";
import { useTranslation } from "react-i18next";
import { useResizeObserver } from "../../../hooks/useResizeObserver";


const GoogleMap = ({onReady, mapPerspective}) => {
  const [view, setView] = useState(null);
  const {isGoogleMap, applyMapColor} = useMapColorState();
  const language = useRecoilValue(googleMapLanguageState);
  const [hidden, setHidden] = useState(!isGoogleMap);
  const [initialized, setInitialized] = useState(false);
  const [googleMapInstance, setGoogleMapInstance] = useState(null);
  const googleMapStyle = useRecoilValue(googleMapStyleState);
  const googleMapThemeName = useRecoilValue(googleMapThemeNameState);
  const {t} = useTranslation();
  const gMapRef = useRef(null);
  const gSearchRef = useRef(null);
 
  useEffect(() => {

    if (isGoogleMap) {
      const view = createView();
      WRAP.Geo.selectView(view);
      WRAP.Geo.switchMap(view);
      view.ready(() => {
        onReady();
      });
    }

    // 初回render時のみ実行するので警告無視
    // eslint-disable-next-line
  },[]);


  useEffectSkipFirst(() => {
    if (isGoogleMap) {
      if (!initialized) {
        const view = createView();
        WRAP.Geo.switchMap(view).then(()=>{
          applyMapColor();
        });
      } else {
        WRAP.Geo.switchMap(view).then(()=>{
          applyMapColor();
        });
      }
    }
    setHidden(!isGoogleMap)
  }, isGoogleMap);

  useEffectSkipFirst(() => {
    if (!initialized) {
      return;
    }
    const newView = createView();
    WRAP.Geo.switchMap(newView).then(() => {
      WRAP.Geo.disposeView('GoogleMaps', view);
    });
  }, language);

  useEffectSkipFirst(() => {
    googleMapInstance.set('styles', googleMapStyle);
  }, googleMapStyle);

  useEffectSkipFirst(() => {
    const mapTypeId = getMapTypeId(googleMapThemeName);
    if (mapTypeId !== googleMapInstance.getMapTypeId()) {
      googleMapInstance.setMapTypeId(mapTypeId);
    }
  }, googleMapThemeName);

  useResizeObserver(gMapRef, () => {
    if (gMapRef.current.clientWidth < 1400) {
      gSearchRef.current.classList.add('google-search-gap-above');
    } else {
      gSearchRef.current.classList.remove('google-search-gap-above');
    }
  });

  const getMapTypeId = (theme) => {
    /* eslint-disable */
    return theme === GOOGLE_MAP_THEME.SATELLITE ? google.maps.MapTypeId.HYBRID : google.maps.MapTypeId.TERRAIN;
    /* eslint-enable */
  };

  const createView = () => {
    if (!window.google) {
      return;
    }
    const mapDiv = document.getElementById('google-map');
    const searchInput = document.getElementById('google-search');
    const zoomRange = getZoomRange();

    /* eslint-disable */
    const gMap = new google.maps.Map(mapDiv, {
      zoom: mapPerspective.zoom,
      minZoom: zoomRange.min_zoom,
      maxZoom: zoomRange.max_zoom,
      center: new google.maps.LatLng(mapPerspective.centerLat, mapPerspective.centerLon),
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      draggableCursor: "inherit",
      mapTypeId: getMapTypeId(googleMapThemeName),
      styles: googleMapStyle
      });
    /* eslint-enable */

    /* eslint-disable */
    const gSearchBox = new google.maps.places.SearchBox(searchInput);
    gMap.controls[google.maps.ControlPosition.TOP_CENTER].push(searchInput);
    gMap.addListener('bounds_changed', () => {
      gSearchBox.setBounds(gMap.getBounds());
    });

    let markerList = [];
    gSearchBox.addListener('places_changed', () => {
      const places = gSearchBox.getPlaces();

      if (places.length === 0) {
        return;
      }

      markerList.forEach(marker => {
        marker.setMap(null);
      });
      markerList = [];

      const bounds = new google.maps.LatLngBounds();

      places.forEach(place => {
        if (!place.geometry || !place.geometry.location) {
          return;
        }

        const icon = {
          url: place.icon,
          size: new google.maps.Size(71, 71),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(17, 34),
          scaledSize: new google.maps.Size(25, 25)
        }

        markerList.push(
          new google.maps.Marker({
            map: gMap,
            icon,
            title: place.name,
            position: place.geometry.location
          })
        );

        if (place.geometry.viewport) {
          bounds.union(place.geometry.viewport);
        } else {
          bounds.extend(place.geometry.location);
        }
      });
      gMap.fitBounds(bounds);
    });
    /* eslint-enable */

    const view = WRAP.Geo.addGoogleMapsView(gMap);
    setGoogleMapInstance(gMap);

    setView(view);
    setInitialized(true);

    return view;
  };

  return (
    <>
      <div id="google-map" className="map" hidden={hidden} ref={gMapRef} />
      <input id="google-search" className="google-search" type="search" placeholder={t("map search")} spellCheck={false} ref={gSearchRef} />
    </>
  )
};

export default GoogleMap;